/**
 * Auth context for the Medicys staff workspace.
 *
 * One global provider mounted at the App root. Three states:
 *   - undefined  : checking session (initial mount, /me in flight)
 *   - null       : not authenticated → ProtectedRoute redirects to /login
 *   - {email,name,role,…} : authenticated, render the page
 *
 * Centralises:
 *   - the `/api/auth/me` bootstrap call on first mount
 *   - axios `withCredentials: true` so cookies ride on every request
 *   - the `signIn`, `signOut`, `refresh` helpers used by Login.jsx
 *   - normalising the FastAPI 422 error shape into a single string
 *     (validation errors come back as ``[{msg, loc, …}, …]`` which
 *     blows up React if rendered directly).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { setStaffCookie } from "./staffCookie";

// All axios calls in the app go through cookies - set this once,
// globally, so individual call sites don't need to remember.
axios.defaults.withCredentials = true;

// Tiny helper for the global retry interceptor below.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Global axios response interceptor - auto-retries ONCE on a transient
 * edge / cold-start failure (Cloudflare 520-524, generic 502/503/504,
 * network errors with no response, or an HTML body to a JSON request).
 *
 * Why globally? On production, the FIRST API call after a quiet period
 * hits an unwarmed backend worker. Cloudflare's edge gives up before
 * the worker finishes booting and returns a 520 HTML page. A second
 * attempt 800ms later catches a warm worker and just works. Lifting
 * this into an interceptor means EVERY component (login, /share mint,
 * email intake, deals page, ...) gets the same behaviour for free -
 * no per-component wiring needed.
 *
 * Guards against infinite loops: each retried request is tagged with
 * ``__coldStartRetried`` on its config. On the second failure the
 * interceptor passes through so the caller can show its own error.
 */
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;
    if (!config || config.__coldStartRetried) {
      return Promise.reject(error);
    }
    if (!isTransientEdgeError(error)) {
      return Promise.reject(error);
    }
    config.__coldStartRetried = true;
    await sleep(800);
    return axios(config);
  }
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthCtx = createContext({
  user: undefined,
  signIn: () => {},
  signOut: () => {},
  refresh: () => {},
});

export function formatApiError(detail, fallback = "Something went wrong.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return (
      detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(" ") || fallback
    );
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * True when an axios error looks like a transient edge / cold-start
 * failure rather than a real backend exception. We see this on
 * production when the first request after a quiet period hits a
 * cold backend worker and Cloudflare returns a 520/521/522/523/524
 * HTML page before the upstream finishes warming. The body of that
 * response is HTML, not JSON, so the usual ``error.response.data
 * .detail`` extraction gives garbage - surfacing Cloudflare's prose
 * verbatim ("The origin web server sent a response that Cloudflare
 * could not parse…") into the sign-in box.
 *
 * For these cases the right UX is to *retry once* silently with a
 * short delay, and only surface a "service warming up" message if
 * the retry also fails. ``isTransientEdgeError`` is the predicate
 * the calling sites use to decide which path to take.
 */
export function isTransientEdgeError(err) {
  if (!err) return false;
  // Network failures (no response at all) - often dropped TCP, DNS,
  // or in-flight request abort. Worth a single retry.
  if (!err.response) return true;
  const status = err.response.status;
  // Cloudflare 520-524 are the edge-can-not-parse / timeout family.
  // Generic 502/503/504 from any reverse proxy fit the same bucket.
  if (status >= 520 && status <= 524) return true;
  if ([502, 503, 504].includes(status)) return true;
  // Non-JSON response body to a JSON request - almost always means
  // an HTML error page slipped through. Treat as transient.
  const data = err.response.data;
  if (typeof data === "string" && /<\s*html|cloudflare/i.test(data)) return true;
  // FastAPI returns {"detail": "<string>"} on JSON errors. If the
  // detail itself contains the literal Cloudflare error wording, we
  // know we're looking at a wrapped edge page (some proxies wrap the
  // origin response into a JSON envelope) - treat as transient too.
  if (data && typeof data === "object" && typeof data.detail === "string") {
    if (/cloudflare|origin web server/i.test(data.detail)) return true;
  }
  return false;
}

/**
 * Universal error-renderer for any axios catch block in the app. Use
 * this instead of hand-rolled ``err.response.data.detail`` extraction
 * so transient edge errors (Cloudflare 520, 502-504, network drops)
 * get a friendly "service warming up" line rather than Cloudflare's
 * raw HTML.
 *
 * The global axios interceptor has already retried once by the time
 * this is called - so reaching here on a transient error means the
 * retry ALSO failed, which deserves the friendlier user-facing copy.
 */
export function renderApiError(err, fallback = "Something went wrong.") {
  if (isTransientEdgeError(err)) {
    return (
      "The server took too long to respond - it may be waking up or " +
      "busy with a long request. Please try again in a moment."
    );
  }
  return formatApiError(err?.response?.data?.detail, fallback);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.user || null);
      return data.user || null;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  // Bootstrap once on mount.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Whenever the user transitions from "no session" → "authenticated",
  // drop the ms_staff cookie. The analytics tracker reads it both
  // client-side and server-side so any /p/{token} open from this
  // browser is recorded as ``source: "staff-preview"`` and excluded
  // from the client-activity feed. Previously this lived only inside
  // ShareProposal's useEffect, which meant logging in and going
  // straight to /pipeline or /proposal-search left the cookie unset.
  useEffect(() => {
    if (user) setStaffCookie();
  }, [user]);

  const signOut = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* even if the server call fails (offline, etc), clear local state */
    }
    setUser(null);
  }, []);

  // ``signIn`` is intentionally NOT exposed - Login.jsx hits the
  // begin-login / complete-login / confirm-totp endpoints directly
  // because the multi-step flow needs UI control over which step is
  // in progress. After a successful login we call ``refresh()`` to
  // hydrate the context.

  const value = useMemo(
    () => ({ user, refresh, signOut }),
    [user, refresh, signOut]
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}

export { API };
